// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { user_roles, initRolesMap, getVisibleRoles } from "./lib/userRoles";
import type { UserRolesMap } from "./lib/userRoles";
import { USER_TAGS } from "@/lib/CommonTags.generated";

// models
import type { RegisterUserObj } from "@/lib/models";
import type { ISetUserTagRes } from "@/lib/backend/users.generated.types";

// backend
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import * as Auth from "@/lib/auth";
import type { IOauthProvider } from "@/lib/auth";

// import subcomponents
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import Toggle from "react-toggle";

import "./UserNew.scss";

import img_banner from "@/img/banner.png";

type Props = {
    match: match<{ user_uuid?: string }>;
    history: History;
    location: Location;
};

type State = {
    active: boolean;
    title: string;
    username: string;
    password: string;
    password2: string;
    accept_terms: boolean;
    roles_map: UserRolesMap;
    error: string;
    warning: string;
    providers: IOauthProvider[];
    // currently selected item of a dropdown menu
    selected_item: IOauthProvider | null;
    external_username: string;
    use_external: boolean;
};

const oauthProviders = Auth.getOAuthProviders();

/**
 * Form to create new user.
 */
class UserNew extends Component<Props, State> {
    state: State = {
        active: true,
        title: "",
        username: "",
        password: "",
        password2: "",
        accept_terms: true,
        roles_map: initRolesMap(),
        error: "",
        warning: "",
        providers: [],
        external_username: "",
        use_external: false,
        selected_item: null,
    };

    async componentDidMount() {
        try {
            const providers = await Auth.oauth2Providers();
            const items_available = oauthProviders.filter(
                (x: IOauthProvider) => providers.indexOf(x.value) >= 0 || x.value === "up"
            );
            if (items_available.length > 0) {
                this.setState({ providers: items_available, selected_item: items_available[0] });
            } else {
                this.setState({ providers: items_available, selected_item: null });
            }
        } catch (err) {
            console.log(err);
        }
    }

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        // read new values
        const target = event.currentTarget;

        this.setState(prev_state => {
            // hack for toggle element
            if (user_roles.find(role => role.id === target.name)) {
                prev_state.roles_map.set(target.name, (target as HTMLInputElement).checked);
            } else {
                // update new datasource placeholder
                prev_state[target.name] = target.value;
            }

            return prev_state;
        });
    };

    setRole = async (user_uuid: string, role_id: string): Promise<ISetUserTagRes> =>
        role_id === USER_TAGS.kiosk
            ? // Special case for kiosk role
              await getBackend2().users.setUserTag({ id: user_uuid, name: USER_TAGS.kiosk, value: "true" })
            : await getBackend2().users.addRoleForUser({ id: user_uuid, role: role_id });

    validateRoles = (): boolean => {
        // Check if at least one role is selected
        return [...this.state.roles_map.values()].some(value => value);
    };

    handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();

        try {
            const username = this.state.username ? this.state.username.trim() : this.state.username;
            // send collected parameters through validation
            const user: RegisterUserObj = {
                title: this.state.title,
                username: username,
                password: this.state.password,
                password2: this.state.password2,
                accept_terms: this.state.accept_terms,
                provider: this.state.use_external ? (this.state.selected_item?.value ?? null) : null,
                provider_username: this.state.external_username,
            };
            if (this.state.use_external) {
                user.password = "lkjLKJ098=)(";
                user.password2 = user.password;
            }

            let message = "";
            message = Auth.validate(user);

            if (message === "" && !this.validateRoles()) {
                message = "Please select at least one role";
            }

            this.setState({ warning: message });

            if (message === "") {
                const user_uuid = await Auth.addUser(user);
                this.setState({ error: "" });

                const promises = user_roles
                    .filter(role => this.state.roles_map.get(role.id))
                    .map(role => this.setRole(user_uuid, role.id));

                await Promise.all(promises);
                this.props.history.push("/admin/users");
            }
        } catch (err) {
            this.setState({ error: (err as Error).message });
        }
    };

    renderExternalLogins() {
        if (this.state.providers.length === 0) {
            return null;
        }

        return (
            <div className="form-group">
                <label>- {this.state.selected_item?.field_caption ?? ""}</label>
                <input
                    name="external_username"
                    type="text"
                    className="form-control"
                    placeholder=""
                    value={this.state.external_username}
                    onChange={this.handleInputChange}
                />
            </div>
        );
    }

    render() {
        const org_code = Auth.getOrgData().code;
        return (
            <div key="ds_new" className="user-new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    <div className="tab-content">
                                        <h4>
                                            <FormattedMessage
                                                id="User_new.add_new_user"
                                                defaultMessage="Add new user"
                                            />
                                        </h4>
                                        <ErrorComponent msg={this.state.error} type="error" />
                                        <ErrorComponent msg={this.state.warning} type="warning" />
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.title" defaultMessage="Title" />:
                                            </label>
                                            <FormattedMessage
                                                id="common.first_and_last_name"
                                                defaultMessage="First and last name"
                                            >
                                                {text => (
                                                    <input
                                                        name="title"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={String(text)}
                                                        value={this.state.title}
                                                        onChange={this.handleInputChange}
                                                    />
                                                )}
                                            </FormattedMessage>
                                        </div>
                                        <div className="form-group">
                                            <label>
                                                <FormattedMessage id="common.username" defaultMessage="Username" />:
                                            </label>
                                            <FormattedMessage id="common.email" defaultMessage="Email address">
                                                {text => (
                                                    <input
                                                        name="username"
                                                        type="text"
                                                        className="form-control"
                                                        autoComplete="new-password"
                                                        placeholder={String(text)}
                                                        value={this.state.username}
                                                        onChange={this.handleInputChange}
                                                    />
                                                )}
                                            </FormattedMessage>
                                        </div>
                                        {!this.state.use_external && (
                                            <div className="form-group">
                                                <label>
                                                    <FormattedMessage id="common.password" defaultMessage="Password" />:
                                                </label>
                                                <input
                                                    name="password"
                                                    type="password"
                                                    className="form-control"
                                                    autoComplete="new-password"
                                                    value={this.state.password}
                                                    onChange={this.handleInputChange}
                                                />
                                            </div>
                                        )}
                                        {!this.state.use_external && (
                                            <div className="form-group">
                                                <label>
                                                    <FormattedMessage
                                                        id="common.reenter_password"
                                                        defaultMessage="Re-enter password"
                                                    />
                                                    :
                                                </label>
                                                <input
                                                    name="password2"
                                                    type="password"
                                                    className="form-control"
                                                    value={this.state.password2}
                                                    onChange={this.handleInputChange}
                                                />
                                            </div>
                                        )}
                                        {this.state.use_external && (
                                            <div className="form-group">
                                                <label>{this.state.selected_item?.field_caption ?? ""}</label>
                                                <input
                                                    name="external_username"
                                                    type="text"
                                                    className="form-control"
                                                    placeholder=""
                                                    value={this.state.external_username}
                                                    onChange={this.handleInputChange}
                                                />
                                            </div>
                                        )}
                                        <div className="form-group">
                                            <div className="user-roles d-flex flex-wrap">
                                                {getVisibleRoles({ is_admin: true, org_code }).map(role => (
                                                    <div className="form-group" key={`toggle-user-role-${role.id}`}>
                                                        <label>
                                                            <FormattedMessage id={role.label_id} />:
                                                        </label>
                                                        <br />
                                                        <Toggle
                                                            name={role.id}
                                                            checked={this.state.roles_map.get(role.id)}
                                                            onChange={this.handleInputChange}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="button-list order-last ml-auto">
                                            <button className="btn btn-outline-primary" onClick={this.handleSubmit}>
                                                <FormattedMessage id="common.add" defaultMessage="Add" />
                                            </button>
                                            <Link className="btn btn-outline-secondary" to="/admin/users">
                                                <FormattedMessage id="common.back" defaultMessage="Back" />
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}

export default UserNew;
